import { useCreate, useRedirect } from "ra-core";
import { useSearchParams, Link } from "react-router";
import { useState } from "react";
import {
  ArrowLeft,
  Receipt,
  Briefcase,
  FileText,
  Calendar,
  DollarSign,
  Save,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  Proposal,
  Job,
} from "../../providers/fakerest/dataGenerator/types";
import { JobSearchSelect } from "../jobs/JobSearchSelect";
import { JobCreateDialog } from "../jobs/JobCreateDialog";
import { ProposalSearchSelect } from "../proposals/ProposalSearchSelect";
import { ProposalCreateDialog } from "../proposals/ProposalCreateDialog";

const statusChoices = [
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
];

export const InvoiceCreate = () => {
  const [searchParams] = useSearchParams();
  const defaultJobId = searchParams.get("job_id");

  const [create, { isPending: isCreating }] = useCreate();
  const redirect = useRedirect();

  const [jobId, setJobId] = useState<string>(defaultJobId || "");
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [proposalId, setProposalId] = useState<string>("");
  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);
  const [amount, setAmount] = useState<number>(0);
  const [status, setStatus] = useState<string>("pending");
  const [dueDate, setDueDate] = useState<string>(
    // Default to 30 days from now
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  );

  const handleJobCreated = (job: Job) => {
    setJobId(String(job.id));
    // Reset proposal when job changes
    setProposalId("");
    setAmount(0);
  };

  const handleJobChange = (newJobId: string) => {
    setJobId(newJobId);
    // Reset proposal when job changes
    setProposalId("");
    setAmount(0);
  };

  const handleProposalChange = (newProposalId: string) => {
    setProposalId(newProposalId);
  };

  const handleProposalCreated = (proposal: Proposal) => {
    setProposalId(String(proposal.id));
    setAmount(proposal.total);
  };

  const handleSubmit = () => {
    if (!jobId) {
      alert("Please select a job");
      return;
    }
    if (!proposalId) {
      alert("Please select a proposal");
      return;
    }

    create(
      "invoices",
      {
        data: {
          job_id: Number(jobId),
          proposal_id: Number(proposalId),
          amount,
          status,
          due_date: dueDate,
          paid_at: status === "paid" ? new Date().toISOString() : null,
        },
      },
      {
        onSuccess: (data) => {
          redirect("show", "invoices", data.id);
        },
      },
    );
  };

  return (
    <div className="p-4 max-w-2xl mx-auto">
      {/* Back button */}
      <Link
        to="/invoices"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Invoices
      </Link>

      {/* Header */}
      <h1 className="text-2xl font-semibold mb-6 flex items-center gap-2">
        <Receipt className="h-6 w-6" />
        Create New Invoice
      </h1>

      {/* Form Card */}
      <Card>
        <CardHeader>
          <CardTitle>Invoice Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Job Selector */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Job <span className="required-asterisk">*</span>
            </Label>
            <JobSearchSelect
              value={jobId}
              onChange={setJobId}
              placeholder="Search for a job..."
              onCreateNew={() => setJobDialogOpen(true)}
            />
          </div>

          {/* Proposal Selector */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Proposal <span className="required-asterisk">*</span>
            </Label>
            <ProposalSearchSelect
              value={proposalId}
              onChange={setProposalId}
              jobId={jobId}
              disabled={!jobId}
              onCreateNew={
                jobId ? () => setProposalDialogOpen(true) : undefined
              }
            />
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Amount
            </Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="w-48"
            />
            {proposalId && (
              <p className="text-sm text-muted-foreground">
                Auto-filled from selected proposal
              </p>
            )}
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusChoices.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Due Date
            </Label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-48"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSubmit}
              disabled={isCreating || !jobId || !proposalId}
            >
              <Save className="h-4 w-4 mr-2" />
              {isCreating ? "Creating..." : "Create Invoice"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Job Create Dialog */}
      <JobCreateDialog
        open={jobDialogOpen}
        onOpenChange={setJobDialogOpen}
        onSuccess={handleJobCreated}
      />

      {/* Proposal Create Dialog */}
      <ProposalCreateDialog
        open={proposalDialogOpen}
        onOpenChange={setProposalDialogOpen}
        jobId={jobId}
        onSuccess={handleProposalCreated}
      />
    </div>
  );
};
